import { useEffect, forwardRef, useRef } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';

// Hooks
import { useDatepicker } from '@/hooks/datepicker/useDatepicker';
import { UPDATE_MIN_DATE, UPDATE_PERIOD } from '@/utils/hooks/datepicker/actions';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import useUser from '@/utils/hooks/swr/useUser';

// Libs
import { PREVIOUS_PERIOD, LAST_30_DAYS } from '@/lib/periods';
import { pendoTrack } from '@/components/tools/Pendo';
import { localStorageDatepickerAnalytics } from '@/lib/analytics/global';
import datepickerDateFormat from '@/lib/datepicker/dateFormat';
import { dayOfWeek } from '@/lib/scheduler/calendar/calendar';
import { PENDO_EVENT_ANALYTICS_DATEPICKER_PERIOD } from '@/lib/tools/pendo/events';

// Components
import Datepicker from '@/components/base/Datepicker';
import DatepickerInput from '@/components/base/Datepicker/DatepickerInput';
import DatepickerAnalyticsBottomSection from '@/components/composite/Datepicker/DatepickerAnalyticsBottomSection';

dayjs.extend(utc);
dayjs.extend(timezone);

const DatepickerAnalyticsContent = forwardRef(
    (
        { noInputBorder, toggleDatepicker, setReferenceElement, isLeftIcon, placeholder, minDate },
        ref
    ) => {
        const { dispatch, datepickerData } = useDatepicker();

        useEffect(() => {
            // if selected date is before the new min date, update the period to default
            if (dayjs(datepickerData.date?.[0]).isBefore(minDate)) {
                dispatch({ type: UPDATE_PERIOD, payload: LAST_30_DAYS });
            }
            dispatch({
                type: UPDATE_MIN_DATE,
                payload: minDate
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [minDate]);

        return (
            <DatepickerInput
                ref={ref}
                placeholder={placeholder}
                isLeftIcon={isLeftIcon}
                noBorder={noInputBorder}
                setReferenceElement={setReferenceElement}
                toggleDatepicker={toggleDatepicker}
            />
        );
    }
);

DatepickerAnalyticsContent.displayName = 'DatepickerAnalyticsContent';

const DatepickerAnalytics = ({ periodSelected, setPeriodSelected }) => {
    const { maxDate, getDataRetentionDate, minDate } = useDateLimits();
    const { user } = useUser();
    const inputRef = useRef(null);

    const newMinDate = minDate ?? getDataRetentionDate();

    useEffect(() => {
        localStorage.setItem(localStorageDatepickerAnalytics, JSON.stringify(periodSelected));
    }, [periodSelected]);

    return (
        <Datepicker
            date={periodSelected.date ?? null}
            periods="analytics"
            periodSelected={periodSelected.period ?? LAST_30_DAYS}
            timezone={user?.settings.timezone}
            firstDayOfWeek={dayOfWeek[user?.settings?.first_day_week ?? 'monday']}
            bottomSection={
                <DatepickerAnalyticsBottomSection
                    compareTo={periodSelected.compareTo ?? PREVIOUS_PERIOD}
                    onChange={(el) => {
                        setPeriodSelected((prevState) => ({
                            ...prevState,
                            compareTo: el.value
                        }));
                    }}
                />
            }
            options={{
                placement: 'bottom-end',
                dateFormat: datepickerDateFormat[user.settings.date_format] ?? 'Y/m/d',
                mode: 'range',
                parseDate: (dateStr) => {
                    return dayjs(dateStr).toDate();
                },
                maxDate,
                minDate: newMinDate
            }}
            onChange={(data) => {
                if (data.date && data.date.length === 2) {
                    setPeriodSelected((prevState) => ({
                        ...prevState,
                        date: data.date,
                        period: data.periodSelected
                    }));

                    // Pendo : track event
                    pendoTrack(PENDO_EVENT_ANALYTICS_DATEPICKER_PERIOD);
                }
            }}>
            <DatepickerAnalyticsContent minDate={newMinDate} ref={inputRef} />
        </Datepicker>
    );
};

DatepickerAnalytics.propTypes = {
    periodSelected: PropTypes.object,
    setPeriodSelected: PropTypes.func
};

export default DatepickerAnalytics;
